// src/About.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDocumentMeta from './useDocumentMeta';
import useCoachesCatalog from './hooks/useCoachesCatalog';
import { coachRoleLine } from './utils/coachesCatalog';
import './About.css';

import aboutHeroVideo from './assets/videos/16x9_GlassCity Hockey.mp4';
import aboutStoryVideo from './assets/videos/GC_GLADIATIORS_FINAL.MOV';

const TESTIMONIALS = [
  { quote: 'Best decision we made for our son. The coaches care about development and character, not just wins.', author: 'Gladiator parent' },
  { quote: "It's not just hockey — it's a family. The kids push each other and the parents support each other.", author: 'Gladiator family' },
  { quote: 'Affordable travel hockey that actually invests in the kids. We couldn’t ask for more.', author: 'Gladiator parent' },
];

const About = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const { coaches } = useCoachesCatalog();

  useDocumentMeta({
    title:       'About - Glass City Gladiators',
    description: 'Discover how our nonprofit travel hockey program in Toledo puts families and players first, offering top-tier coaching, affordable dues, and a lifelong community.',
    canonical:   'https://gcghockey.com/about',
    ogImage:     'https://gcghockey.com/assets/og-about.png',
    ogType:      'website'
  });

  useEffect(() => {
    const timestamp = new Date().getTime();
    const tsvUrl = `https://docs.google.com/spreadsheets/d/e/2PACX-1vS068-sX2VEWrswmUdXiYtbVHRVwL_3OtOJE96GLWgWlYqeT5TSrVEzsOUXat72jt_2-oDd-L96UeQF/pub?gid=0&single=true&output=tsv&t=${timestamp}`;

    fetch(tsvUrl)
      .then(res => res.text())
      .then(tsv => {
        const [headerLine, ...lines] = tsv.trim().split('\n');
        const headers = headerLine.split('\t').map(h => h.trim());
        const idx = h => headers.indexOf(h);

        const tournamentsData = lines.map(line => {
          const cols = line.split('\t');
          while (cols.length < headers.length) cols.push('');
          return {
            name: cols[idx('Tournament Name')] || '',
            location: cols[idx('Location')] || '',
            dates: cols[idx('Dates')] || '',
            organizer: cols[idx('Organizer')] || ''
          };
        });

        setTournaments(tournamentsData);
        setLoadingTournaments(false);
      })
      .catch(() => setLoadingTournaments(false));
  }, []);

  return (
    <div className="about-page simplified">
      <div className="ice-texture-bg" aria-hidden="true" />

      {/* Hero */}
      <header className="about-hero">
        <div className="about-hero-video-wrap">
          <video
            className="about-hero-video"
            src={aboutHeroVideo}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          />
          <div className="about-hero-overlay" />
        </div>
        <div className="about-hero-content">
          <h1 className="about-hero-title">More Than a Team — a Family on Ice</h1>
          <p className="about-hero-subhead">
            Toledo’s nonprofit 12U travel hockey program where development, character, and community come first.
          </p>
          <Link to="/join-us" className="about-hero-cta">See what we’re about</Link>
        </div>
      </header>

      {/* Mission */}
      <section className="about-mission">
        <div className="about-mission-inner">
          <h2 className="about-section-heading">Why we exist</h2>
          <p className="about-mission-text">
            We believe every kid who wants to compete deserves the chance — without breaking the bank. 
            Glass City Gladiators is a nonprofit, family-first program run by parents and coaches who’ve been there. 
            We put development and character ahead of wins, and we invest every dollar back into our athletes and our community.
          </p>
        </div>
      </section>

      {/* In action video */}
      <section className="about-video-section">
        <h2 className="about-section-heading">See the Gladiators in action</h2>
        <div className="about-video-section-wrap">
          <video
            className="about-video-section-video"
            src={aboutStoryVideo}
            controls
            playsInline
            aria-label="Glass City Gladiators highlight video"
          />
        </div>
      </section>

      {/* What you get - Season highlights */}
      <section className="season-highlights">
        <h3>What’s ahead this season (2025–2026)</h3>
        <div className="highlight-list">
          <div className="highlight-item">
            <div className="highlight-icon">🏆</div>
            <strong>9 tournaments</strong>
            <span>Travel, compete, and bond as a team.</span>
          </div>
          <div className="highlight-item">
            <div className="highlight-icon">🏒</div>
            <strong>Up to 50 games</strong>
            <span>Real competition and real growth.</span>
          </div>
          <div className="highlight-item">
            <div className="highlight-icon">⚡</div>
            <strong>Practice 2x per week</strong>
            <span>Skill development and systems you can count on.</span>
          </div>
          <div className="highlight-item">
            <div className="highlight-icon">🎯</div>
            <strong>Private instruction weekly</strong>
            <span>With Coach Nick Parillo — an extra edge for every player.</span>
          </div>
          <div className="highlight-item">
            <div className="highlight-icon">💪</div>
            <strong>Dryland 1–2x per week</strong>
            <span>Stronger, faster, and more confident on the ice.</span>
          </div>
          <div className="highlight-item">
            <div className="highlight-icon">🌟</div>
            <strong>Game at the Huntington Center</strong>
            <span>A pro-style experience they’ll never forget.</span>
          </div>
        </div>
      </section>

      {/* Who we are - Coaches */}
      <section className="about-coaches">
        <h2 className="about-section-heading">Behind the bench</h2>
        <p className="about-coaches-intro">Coaches and parents who care about development and culture.</p>
        <div className="about-coaches-grid">
          {coaches.map((c) => (
            <div key={c.id || c.name} className="about-coach-card">
              {c.photoUrl ? (
                <img src={c.photoUrl} alt={c.name} className="about-coach-photo" />
              ) : (
                <div className="about-coach-avatar">{c.name.charAt(0)}</div>
              )}
              <h4 className="about-coach-name">{c.name}</h4>
              <p className="about-coach-role">{c.role || coachRoleLine(c)}</p>
              {c.tagline ? <p className="about-coach-tagline">{c.tagline}</p> : null}
              {c.bio ? <p className="about-coach-bio">{c.bio}</p> : null}
            </div>
          ))}
        </div>
      </section>

      {/* What families say */}
      <section className="about-testimonials">
        <h2 className="about-section-heading">What families say</h2>
        <div className="about-testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={i} className="about-testimonial">
              <p className="about-testimonial-quote">"{t.quote}"</p>
              <footer className="about-testimonial-author">— {t.author}</footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* Tournament schedule */}
      <section className="tournament-schedule">
        <h3>2025–2026 tournament schedule</h3>
        {loadingTournaments ? (
          <p className="about-loading">Loading tournament schedule...</p>
        ) : tournaments.length > 0 ? (
          <div className="table-wrapper">
            <table className="tournament-table">
              <thead>
                <tr>
                  <th>Tournament Name</th>
                  <th>Location</th>
                  <th>Dates</th>
                  <th>Organizer</th>
                </tr>
              </thead>
              <tbody>
                {tournaments.map((tournament, index) => (
                  <tr key={index}>
                    <td data-label="Tournament">{tournament.name}</td>
                    <td data-label="Location">{tournament.location}</td>
                    <td data-label="Dates">{tournament.dates}</td>
                    <td data-label="Organizer">{tournament.organizer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="about-empty">Tournament schedule coming soon!</p>
        )}
      </section>

      {/* Final CTA */}
      <section className="about-cta">
        <h2 className="about-cta-title">Ready to join the Gladiators?</h2>
        <p className="about-cta-sub">See how to get on the ice with us.</p>
        <Link to="/join-us" className="about-cta-btn">Join the team</Link>
      </section>
    </div>
  );
};

export default About;
